import { z } from 'zod';

/**
 * Pagamenti RT e stato di sincronizzazione reparti delle stampanti fiscali.
 *
 * La configurazione reparti per stampante NON è più un JSONB: vive nella
 * tabella `pos_fiscal_printer_departments` (DTO in ./fiscal-departments).
 *
 * Post-#3188 il trattamento pagamento RT si risolve da
 * `pos_payment_methods.ade_code` (NOT NULL): nessun mapping per-stampante.
 *
 * Trattamenti pagamento RT (corrispettivi, tracciato XML 7.0):
 * i valori sono le categorie native che ogni registratore telematico
 * conosce e aggrega nel file giornaliero verso l'AdE:
 *   0 = Contanti          → PagatoContanti
 *   1 = Elettronico       → PagatoElettronico
 *   2 = Non riscosso      → campi non riscosso per-aliquota
 *   3 = Buoni pasto       → blocco Ticket
 *   5 = Sconto a pareggio → ScontoApagare (include i buoni multiuso)
 * Il valore 4 ("Buono multiuso") è dismesso: era un doppione — nel
 * tracciato 7.0 i buoni multiuso confluiscono in ScontoApagare (5).
 * La colonna DB resta `ade_code` per continuità storica.
 */

/**
 * Sotto-tipo del "non riscosso" (ade_code = 2): obbligatorio per quel codice,
 * vietato altrove (CHECK DB `chk_pos_pm_unpaid_kind`). Su Epson pilota
 * l'`index` di `printRecTotal` (paymentType=2): 1 servizi, 2 beni,
 * 3 fattura (segue fattura), 4 SSN, 5 omaggio.
 */
export const UNPAID_KIND_VALUES = ['services', 'goods', 'invoice', 'ssn', 'gift'] as const;
export type UnpaidKind = (typeof UNPAID_KIND_VALUES)[number];
export const UnpaidKindSchema = z.enum(UNPAID_KIND_VALUES);
export const UNPAID_KIND_LABELS: Record<UnpaidKind, string> = {
  services: 'Non riscosso — servizi',
  goods: 'Non riscosso — beni',
  invoice: 'Non riscosso — segue fattura',
  ssn: 'Non riscosso — SSN',
  gift: 'Non riscosso — omaggio',
};
/** Indice Epson `printRecTotal` per paymentType=2. */
export const UNPAID_KIND_EPSON_INDEX: Record<UnpaidKind, 1 | 2 | 3 | 4 | 5> = {
  services: 1, goods: 2, invoice: 3, ssn: 4, gift: 5,
};
export function isUnpaidKind(v: unknown): v is UnpaidKind {
  return typeof v === 'string' && (UNPAID_KIND_VALUES as readonly string[]).includes(v);
}
/** Coerenza ade_code ↔ unpaid_kind (stessa regola del CHECK DB). */
export function unpaidKindCoherenceError(adeCode: number | null | undefined, unpaidKind: UnpaidKind | null | undefined): string | null {
  if (adeCode === 2 && !unpaidKind) return 'Il trattamento "Non riscosso" richiede il sotto-tipo (servizi, beni, fattura, SSN, omaggio)';
  if (adeCode !== 2 && unpaidKind) return 'Il sotto-tipo non riscosso è ammesso solo con trattamento "Non riscosso"';
  return null;
}

export const ADE_PAYMENT_CODE_VALUES = [0, 1, 2, 3, 5] as const;
export type AdePaymentCode = typeof ADE_PAYMENT_CODE_VALUES[number];

export const ADE_PAYMENT_CODE_LABELS: Record<AdePaymentCode, string> = {
  0: 'Contanti',
  1: 'Elettronico',
  2: 'Non riscosso',
  3: 'Buoni pasto',
  5: 'Sconto a pareggio',
};

/** Task #1659 — Type guard per `AdePaymentCode`: 0..5 numerici interi.
 *  Single source of truth dopo il collasso di `ade_category` (#1659). */
export function isAdePaymentCode(value: unknown): value is AdePaymentCode {
  return typeof value === 'number'
    && Number.isInteger(value)
    && (ADE_PAYMENT_CODE_VALUES as readonly number[]).includes(value);
}

/**
 * Task #3323 — Trattamenti RT selezionabili dall'operatore (modale metodi).
 * Lo Sconto a pareggio (5) resta un valore VALIDO del motore (omaggi a
 * pareggio, riconosciuto via ade_code===5) ma NON è più offerto come scelta
 * nel catalogo metodi né in cassa.
 */
export const ADE_PAYMENT_CODE_SELECTABLE = [0, 1, 2, 3] as const;
export type AdePaymentCodeSelectable = typeof ADE_PAYMENT_CODE_SELECTABLE[number];

/** Type guard per i trattamenti RT selezionabili ({0,1,2,3}, MAI 5). */
export function isAdePaymentCodeSelectable(value: unknown): value is AdePaymentCodeSelectable {
  return typeof value === 'number'
    && Number.isInteger(value)
    && (ADE_PAYMENT_CODE_SELECTABLE as readonly number[]).includes(value);
}

/**
 * Task #1658 — contratto API per `GET /api/pos/fiscal-printers/sync-status`.
 * Per ogni stampante fiscale del tenant ritorna lo stato di sincronizzazione
 * del mapping reparti IVA, derivato confrontando l'hash della configurazione
 * corrente con quello dell'ultima push registrata.
 */
export type FiscalMappingSyncState =
  | 'aligned'        // hash corrente == hash ultima push OK → Allineata
  | 'drift'          // mapping configurato ma mai pushato o hash mismatch → Da sincronizzare
  | 'push_failed'    // ultima push fallita → Push fallito
  | 'runtime_only'   // driver non supporta push (es: A-Cube, Custom pagamenti) → Runtime-only
  | 'empty';         // mapping vuoto e driver supporta push → nessun dato da pushare

export interface FiscalMappingKindStatus {
  state: FiscalMappingSyncState;
  capable: boolean;
  current_hash: string | null;
  last_pushed_at: string | null;
  last_pushed_hash: string | null;
  last_error_code: string | null;
  last_error_message: string | null;
  entry_count: number;
}

export interface FiscalPrinterSyncStatus {
  printer_id: string;
  brand: string;
  departments: FiscalMappingKindStatus;
}

/**
 * Task #1658 — labels IT + classi Tailwind centralizzate per i badge di
 * sincronizzazione mapping (riusati da lista printer e modal dettaglio).
 * Tutto qui per evitare duplicazione cross-componente.
 */
export interface FiscalMappingSyncLabel {
  label: string;
  description: string;
  badgeClass: string;
}

export const FISCAL_SYNC_LABELS: Record<FiscalMappingSyncState, FiscalMappingSyncLabel> = {
  aligned: {
    label: 'Allineata',
    description: 'Mapping configurato in cassa identico a quello programmato sul registratore.',
    badgeClass: 'bg-emerald-500/10 text-emerald-600 border-emerald-500/30',
  },
  drift: {
    label: 'Da sincronizzare',
    description: 'Il mapping in cassa differisce da quello programmato. Esegui la push per allineare il registratore.',
    badgeClass: 'bg-amber-500/10 text-amber-600 border-amber-500/30',
  },
  push_failed: {
    label: 'Push fallita',
    description: "L'ultima programmazione del registratore è fallita. Verifica connessione e riprova.",
    badgeClass: 'bg-red-500/10 text-red-600 border-red-500/30',
  },
  runtime_only: {
    label: 'Runtime-only',
    description: 'Questo modello non supporta la programmazione: il mapping viene applicato a runtime su ogni scontrino.',
    badgeClass: 'bg-slate-500/10 text-slate-600 border-slate-500/30',
  },
  empty: {
    label: 'Non configurato',
    description: 'Mapping vuoto: configura le associazioni nel pannello del registratore.',
    badgeClass: 'bg-muted/40 text-muted-foreground border-border/40',
  },
};
